import React, {useEffect, useState} from 'react';
import {Loading, useEditController, useGetIdentity, useRecordContext} from 'react-admin';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import AppBar from "@mui/material/AppBar";
import {TransitionProps} from "@mui/material/transitions";
import Slide from "@mui/material/Slide";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {ToastUiEditorView} from "toastui-editor";
import VideoPlayer from "./VideoPlayer";

interface TitleDialogFieldProps {
    source: string;
    label?: string;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<unknown>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const BlogCtrbItemTitleDialogField: React.FC<TitleDialogFieldProps> = ({ source, label = '標題' }) => {
    // 使用 useRecordContext 讀取當前記錄
    const record = useRecordContext();
    const id = record ? record.id : undefined;
    const { record: data, isPending: editing, error: editorError } = useEditController({ resource: 'blogctrbitems', id });
    const contentType: number = record ? record['contentType'] : 0;
    const title: string = record ? record[source] : '';
    const weblink: string = record ? record['weblink'] : '';
    const videoLink: string = record ? record['videoLink'] : '';
    const tags: string[] = record ? record['tags'] : '';
    const email: string = record ? record['email'] : '';
    const [txtContent, setTxtContent] = useState<string>('');
    const [initialValue, setInitialValue] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if(data){
            setTxtContent(data.txtContent);
            setInitialValue(data.srcContent || data.txtContent);
        }
    }, [data]);

    useEffect(() => {
        if(initialValue && initialValue !== ''){
            setIsLoading(false);
        }
    }, [initialValue]);

    // 定義狀態控制對話框的打開與關閉
    const [open, setOpen] = useState(false);
    const handleOpen = () => {
        if(contentType === 2){
            window.open(weblink, '_blank');
        }else {
            setOpen(true);
        }
    };
    const handleClose = () => setOpen(false);

    if (editing) {return <Loading />;}
    if (!id || editorError) { return <p>ERROR</p>; }

    return (
        <>
            <Button variant="text" onClick={handleOpen}>
                {title}
            </Button>
            <Dialog fullScreen TransitionComponent={Transition} open={open} onClose={handleClose}>
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            {title}預覽
                        </Typography>
                        <Button autoFocus color="inherit" onClick={handleClose}>
                            返回
                        </Button>
                    </Toolbar>
                </AppBar>
                {
                    contentType === 1 && (
                        <div className="bg-[#e2e2e2] pt-[10px] flex-grow">
                            <div className="bg-white max-w-[930px] mx-auto p-6">
                                <div className="px-5">
                                    <div className="mb-3">
                                        <h1 className="text-3xl font-bold text-blue-gray-900">{title}</h1>
                                    </div>
                                    {/* Metadata */}
                                    <div className="flex flex-col md:flex-row gap-2 md:gap-6 md:items-center mb-[50px] md:mb-10">
                                        <div className="flex gap-2 items-center flex-shrink-0">
                                            <div className="text-[14px] font-bold text-blue-gray-800">
                                                {/* Author Name */}
                                                作者：{email}
                                            </div>
                                            <div className="flex gap-1 items-center text-xs text-blue-gray-500 font-medium">
                                                {/* Date */}
                                                <AccessTimeIcon />

                                                {new Date().toISOString().split('T')[0]}
                                            </div>
                                        </div>
                                        <div className="flex overflow-x-auto whitespace-nowrap gap-2">
                                            {tags?.slice(0,5).map(
                                                (tag: string) => (<div key={tag} className="p-1 bg-gray-100 rounded">{tag}</div>))}
                                        </div>
                                    </div>
                                </div>
                                {isLoading && (<div className="text-red-400 text-center">加載中...</div>)}
                                <ToastUiEditorView key={initialValue} initialValue={initialValue}/>
                            </div>
                        </div>
                    )
                }
                {
                    contentType === 4 && (
                        <div className="bg-[#e2e2e2] pt-[10px] flex-grow">
                            <div className="bg-white max-w-[1280px] mx-auto p-6 mb-[10px]">
                                <div className="mb-6">
                                    <VideoPlayer url={weblink} />
                                </div>
                                <div className="mb-3">
                                    <h1 className="text-3xl font-bold text-blue-gray-900">{title}</h1>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="text-[14px] font-bold text-blue-gray-800">
                                        {/* Author Name */}
                                        作者：{email}
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white max-w-[1280px] mx-auto p-6 ">
                                <div className="flex gap-2 font-medium text-blue-gray-500 text-sm mb-3">
                                    <div>
                                        影片發布時間：
                                        {new Date().toISOString().split('T')[0]}{' '}
                                    </div>
                                </div>
                                {/* Related Tags */}
                                <div className="flex gap-2 mb-5 overflow-x-auto whitespace-nowrap">
                                    {tags?.slice(0,5).map(
                                        (tag: string) => (<div key={tag} className="p-1 bg-gray-100 rounded">{tag}</div>))}
                                </div>
                                {/* Article Content */}
                                <div className="text-blue-gray-900">{txtContent}</div>
                            </div>
                        </div>
                    )
                }
                {
                    contentType === 3 && (
                        <div className="bg-[#e2e2e2] pt-[10px] flex-grow">
                            <div className="bg-white max-w-[1280px] mx-auto p-6 mb-[10px]">
                                <div className="mb-6">
                                    <VideoPlayer url={videoLink} />
                                </div>
                                <div className="mb-3">
                                    <h1 className="text-3xl font-bold text-blue-gray-900">{title}</h1>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="text-[14px] font-bold text-blue-gray-800">
                                        {/* Author Name */}
                                        作者：{email}
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white max-w-[1280px] mx-auto p-6 ">
                                <div className="flex gap-2 font-medium text-blue-gray-500 text-sm mb-3">
                                    <div>
                                        影片發布時間：
                                        {new Date().toISOString().split('T')[0]}{' '}
                                    </div>
                                </div>
                                {/* Related Tags */}
                                <div className="flex gap-2 mb-5 overflow-x-auto whitespace-nowrap">
                                    {tags?.slice(0,5).map(
                                        (tag: string) => (<div key={tag} className="p-1 bg-gray-100 rounded">{tag}</div>))}
                                </div>
                                {/* Article Content */}
                                <div className="text-blue-gray-900">{txtContent}</div>
                            </div>
                        </div>
                    )
                }
            </Dialog>
        </>
    );
};

export default BlogCtrbItemTitleDialogField;
